import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import dynamic from "next/dynamic";
import cn from "classnames";
import mapboxgl from "mapbox-gl";

import { poiListAPI } from "@queries/pois/list/api";
import { validateDestination } from "@queries/destination/detail/api";

import { Breadcrumbs } from "@components/breadcrumbs";
import { SectionHeading } from "@components/headings/section";
import { PoiCard } from "@components/cards/poi";
import { withSave } from "@components/_hocs/withSave";
import { Pagination } from "@components/pagination";
import { Ad } from "@components/ad";
import { NoResults } from "@components/noResults";
import { RelatedPlaces } from "@components/relatedPlaces";

import { keyify } from "@utils/keyify";
import { NextHead } from "@components/nextHead";
import { tagular } from "@cohesion/tagular";

const DynamicMap = dynamic(
  () => import("@components/mapbox/map").then(module => module.Map),
  {
    ssr: false,
  }
);

const SavePoiCard = withSave(PoiCard);
export default function POIList({ place, relatedPlaces, pois, meta }) {
  useEffect(() => {
    const { pathname, search } = document.location;
    const params = new URLSearchParams(search);

    const buildParamStr = ps =>
      ps.reduce((acc, p, i) => {
        const addt = `${i === 0 ? "?" : "&"}${p[0]}=${p[1]}`;

        return acc + addt;
      }, "");

    const paramsStr = buildParamStr(Array.from(params));
    window.history.replaceState({}, "", `${pathname}${paramsStr}`);
  });

  /**
   * Map handling
   */
  const [points, setPoints] = useState(pois.mappoints);
  const [showMap, setShowMap] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [popupData, setPopupData] = useState(null);
  const [bounds, setBounds] = useState(null);
  const mapRef = useRef();
  const hasMinPoiRows = pois.items.length > 17; // assuming 4 items per row and at least 2 remaining for the next row
  const handlePointClick = markerId => () => {
    setActiveItem(prevState => {
      if (prevState === markerId) {
        return null;
      }
      return markerId;
    });
    setPopupData(prevState => {
      const { lat: markerLat, lon: markerLng } = prevState?.coordinates || {};
      const prevId = `${markerLat}-${markerLng}`;
      // if we find a point that matches the previous state's point id, we want to zoom out to bounds
      if (prevId === markerId) {
        mapRef?.current?.fitBounds(bounds, {
          padding: { top: 50, bottom: 50, left: 50, right: 50 },
          maxZoom: 15,
          duration: 1000,
        });
        return null;
      }

      const point = points.find(p => {
        const { lat, lon } = p.coordinates;
        return `${lat}-${lon}` === markerId;
      });

      mapRef?.current?.flyTo({
        center: [point.coordinates.lon, point.coordinates.lat],
        duration: 1000,
      });
      return point;
    });
  };

  useEffect(() => {
    if (Array.isArray(pois?.mappoints) && pois.mappoints.length > 0) {
      setPoints(pois.mappoints);
      setActiveItem(null);
      setPopupData(null);

      const newBounds = new mapboxgl.LngLatBounds();

      pois.mappoints.forEach(point => {
        newBounds.extend([point.coordinates.lon, point.coordinates.lat]);
      });

      setBounds(newBounds);
    }
  }, [pois?.mappoints]);

  const ctx = useMemo(
    () => ({ type: place.meta.type, title: place.title, slug: place.slug }),
    [place.meta.type, place.title, place.slug]
  );

  useEffect(() => {
    if (bounds && mapRef.current) {
      mapRef.current.fitBounds(bounds, {
        padding: { top: 50, bottom: 50, left: 50, right: 50 },
        maxZoom: 15,
        duration: 1000,
      });
    }
  }, [bounds]);

  return (
    <>
      <NextHead>
        <title>{meta.title}</title>
        <link key="canonical" rel="canonical" href={meta.url} />
        <meta key="og:url" property="og:url" content={meta.url} />
        <meta key="title" name="title" content={meta.title} />
        <meta key="og:title" property="og:title" content={meta.title} />
        <meta key="twitter:title" name="twitter:title" content={meta.title} />
        <meta key="description" name="description" content={meta.description} />
        <meta
          key="og:description"
          property="og:description"
          content={meta.description}
        />
        <meta
          key="twitter:description"
          name="twitter:description"
          content={meta.description}
        />
        <meta key="author" name="author" content="Lonely Planet" />
      </NextHead>
      <div className="container">
        <header>
          {place.breadcrumb && (
            <Breadcrumbs
              useCarat
              breadcrumbs={place.breadcrumb.map(item => ({
                ...item,
                slug: `/destinations/${item.slug}`,
              }))}
            />
          )}
          <SectionHeading elem="h1" className="my-8">
            {`Must-see ${pois.category} in ${place.title}`}
          </SectionHeading>
        </header>
        <section>
          <nav className="flex justify-between">
            <div className="items-center justify-between hidden lg:flex">
              <span className="flex flex-col flex-grow">
                <span className="pr-2">Show/Hide Map</span>
              </span>
              <button
                type="button"
                className={cn(
                  showMap ? "bg-blue" : "bg-black-200",
                  "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue focus:ring-offset-2"
                )}
                role="switch"
                aria-checked={showMap}
                aria-labelledby="Show/Hide Map"
                onClick={
                  showMap
                    ? () => {
                        setShowMap(false);
                        tagular("click", {
                          actionOutcome: "SLIDE",
                          outboundUrl: "NULL",
                          webElement: {
                            location: "SECTION",
                            text: "HIDE MAP",
                            elementType: "BUTTON",
                            name: "MAP TOGGLE",
                          },
                        });
                      }
                    : () => {
                        setShowMap(true);
                        tagular("click", {
                          actionOutcome: "SLIDE",
                          outboundUrl: "NULL",
                          webElement: {
                            location: "SECTION",
                            text: "SHOW MAP",
                            elementType: "BUTTON",
                            name: "MAP TOGGLE",
                          },
                        });
                      }
                }
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    showMap ? "translate-x-5" : "translate-x-0",
                    "pointer-events-none inline-block h-5 w-5 transform rounded bg-white shadow ring-0 transition duration-200 ease-in-out"
                  )}
                />
              </button>
            </div>
          </nav>
          <div
            className={cn("mt-6 mb-8 lg:mb-16", {
              "lg:grid lg:grid-cols-12 lg:gap-x-8": showMap,
            })}
          >
            {showMap && (
              <aside className="hidden lg:block h-[480px] row-start-1 col-start-10 col-span-3">
                <DynamicMap
                  height={480}
                  activeItem={activeItem}
                  points={points}
                  onPointClick={handlePointClick}
                  popupData={popupData}
                  mapRef={mapRef}
                  bounds={bounds}
                />
              </aside>
            )}
            {pois.items.length ? (
              <ul
                className={cn(
                  "md:grid space-y-14 md:space-y-0 gap-x-6 gap-y-14 md:grid-cols-12",
                  {
                    "md:col-start-1 md:row-start-1 md:col-span-12 md:row-span-full":
                      showMap,
                  },
                  {
                    "mb-12": pois.pages === 1,
                  }
                )}
              >
                {pois.items.map((poi, i) => (
                  <li
                    key={keyify(poi.slug)}
                    className={cn(
                      "col-span-1",
                      showMap
                        ? "lg:grid-cols-subgrid md:col-span-4 lg:col-span-3"
                        : "md:col-span-3 lg:col-span-3"
                    )}
                  >
                    {poi.esid ? (
                      <SavePoiCard
                        esid={poi.esid}
                        type="poi"
                        hasLargeImage
                        topChoice={poi.meta.isTopChoice}
                        title={poi.title}
                        subtitle={poi?.places?.[0]?.title} // TODO: does POI have place data?
                        description={poi.excerpt}
                        href={`/${poi.slug}`}
                        slug={`/${poi.slug}`}
                        image={
                          poi.images?.length ? poi.images[0] : { url: null }
                        }
                        category={poi.meta.type.slug}
                        hocTagularCtx={{
                          name: "POILIST",
                        }}
                        onClick={() => {
                          tagular("click", {
                            actionOutcome: "INTERNALLINK",
                            outboundUrl: poi.slug,
                            webElement: {
                              location: "SECTION",
                              elementType: "LINK",
                              position: `${i + 1}`,
                              text: poi.title,
                              name: pois.category,
                            },
                          });
                        }}
                      />
                    ) : (
                      <PoiCard
                        hasLargeImage
                        topChoice={poi.meta.isTopChoice}
                        title={poi.title}
                        subtitle={poi?.places?.[0]?.title}
                        description={poi.excerpt}
                        href={`/${poi.slug}`}
                        image={
                          poi.images?.length ? poi.images[0] : { url: null }
                        }
                        category={poi.meta.type.slug}
                        onClick={() => {
                          tagular("click", {
                            actionOutcome: "INTERNALLINK",
                            outboundUrl: poi.slug,
                            webElement: {
                              location: "SECTION",
                              elementType: "LINK",
                              position: `${i + 1}`,
                              text: poi.title,
                              name: `POI-${poi.title}`,
                            },
                          });
                        }}
                      />
                    )}
                  </li>
                ))}
                <li
                  className={cn(
                    "col-start-1 col-span-12",
                    hasMinPoiRows ? "row-start-5" : "row-start-auto" // only splits the results when there are enough rows
                  )}
                >
                  <Ad
                    placement="fullWidth"
                    path="Destinations/POIs/superzone"
                    additionalTargeting={{
                      position: "superzone",
                    }}
                  />
                </li>
                {/**
                 * This element is here to take up space in the right rail to prevent list items
                 * from overlapping the map in the right rail. It is hidden on mobile and invisible to screen
                 * readers and keyboard events.
                 */}
                {showMap && (
                  <li
                    className={cn(
                      "hidden invisible lg:grid lg:grid-cols-subgrid lg:grid-rows-subgrid row-start-1 col-start-10 col-span-3 pointer-events-none",
                      "row-end-[var(--span-count)]" // use css var so that tailwind JIT compiler can pick up the value
                    )}
                    style={{
                      "--span-count": hasMinPoiRows // passes the number of rows plus the extra elements like the ad and this filler element
                        ? parseInt(pois.items.length / 3 + 3, 10)
                        : parseInt(pois.items.length / 3 + 1, 10),
                    }}
                  />
                )}
              </ul>
            ) : (
              <NoResults className="mt-8" query="" error={new Error()} />
            )}
            {pois.pages > 1 && (
              <Pagination
                selectedPage={parseInt(pois.page, 10)}
                numPages={pois.pages > 100 ? 100 : pois.pages}
                className="mt-12 col-span-full"
                formSlug={`/${place.slug}/${pois.category}`}
              />
            )}
            {Array.isArray(relatedPlaces) && relatedPlaces[0] && (
              <div className="col-span-full">
                <RelatedPlaces places={relatedPlaces} ctx={ctx} />
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
}

export async function getServerSideProps({ query, res }) {
  res.setHeader("Cache-Control", "no-cache, max-age=3600, must-revalidate");

  const { place = "", page = 1, poiType } = query;

  /**
   * Exit early if there is no place
   */
  if (!place) {
    return {
      notFound: true,
    };
  }

  /**
   * Normalize the slug value between server/client renders
   */
  const slug = typeof place === "string" ? place : place.join("/");

  const { isValid, redirect } = await validateDestination({
    slug,
  });

  if (!isValid || redirect) {
    return {
      notFound: true,
    };
  }

  const data = await poiListAPI({
    slug,
    category: poiType,
    page,
  });

  /**
   * Check that GQL has returned data for this place, record exists
   */
  if (!data) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      ...data,
    },
  };
}

POIList.propTypes = {
  place: PropTypes.shape({
    breadcrumb: PropTypes.arrayOf(
      PropTypes.shape({ slug: PropTypes.string, title: PropTypes.string })
    ),
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    meta: PropTypes.shape({
      descendants: PropTypes.arrayOf(
        PropTypes.shape({
          slug: PropTypes.string.isRequired,
          title: PropTypes.string.isRequired,
          type: PropTypes.string.isRequired,
        })
      ).isRequired,
      parentPlace: PropTypes.shape({
        title: PropTypes.string.isRequired,
      }).isRequired,
      type: PropTypes.string.isRequired,
    }),
  }).isRequired,
  relatedPlaces: PropTypes.arrayOf(
    PropTypes.shape({
      esid: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      featuredImage: PropTypes.shape({
        alt: PropTypes.string,
        caption: PropTypes.string,
        credit: PropTypes.string,
        height: PropTypes.number,
        name: PropTypes.string,
        title: PropTypes.string,
        url: PropTypes.string,
        width: PropTypes.number,
      }).isRequired,
    }).isRequired
  ).isRequired,

  pois: PropTypes.shape({
    category: PropTypes.string.isRequired,
    pages: PropTypes.number.isRequired,
    page: PropTypes.number.isRequired,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        coordinates: PropTypes.shape({
          lat: PropTypes.string,
          lon: PropTypes.string,
        }),
        excerpt: PropTypes.string.isRequired,
        images: PropTypes.arrayOf(
          PropTypes.shape({
            alt: PropTypes.string,
            url: PropTypes.string,
          })
        ),
        meta: PropTypes.shape({
          isTopChoice: PropTypes.bool.isRequired,
          type: PropTypes.shape({
            title: PropTypes.string.isRequired,
          }).isRequired,
        }),
        slug: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
      })
    ),
    mappoints: PropTypes.arrayOf(
      PropTypes.shape({
        coordinates: PropTypes.shape({
          lat: PropTypes.number,
          lon: PropTypes.number,
        }),
        heading: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        link: PropTypes.shape({
          href: PropTypes.string.isRequired,
        }).isRequired,
        image: PropTypes.shape({
          url: PropTypes.string,
        }),
      })
    ),
  }).isRequired,

  meta: PropTypes.shape({
    url: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  }).isRequired,
};
